import { useEffect, useState } from 'react'

/*
  Text size and colour mode.

  Both are per-device on purpose and live in localStorage rather than in the
  synced database: Papa reading at 130% on his phone should not enlarge the type
  on Anuja's, and a preference that has to reach a Worker before it applies is a
  preference that does nothing on a train with no signal.

  Text size is applied as a scale on the root font size. Every type token in the
  stone theme is expressed in rem, so one property moves the whole type ramp and
  nothing has to be restyled component by component.
*/

export type TextScale = 1 | 1.15 | 1.3 | 1.5
export type ThemeMode = 'light' | 'dark' | 'system'

export const TEXT_SCALES: Array<{ value: TextScale; label: string }> = [
  { value: 1, label: 'Default' },
  { value: 1.15, label: 'Large' },
  { value: 1.3, label: 'Larger' },
  { value: 1.5, label: 'Largest' },
]

const SCALE_KEY = 'travel:textScale'
const MODE_KEY = 'travel:themeMode'

/**
 * Private browsing and blocked site data both make these throw rather than
 * return nothing, and a preference read is not worth a blank screen.
 */
function read<T>(key: string, ok: (v: unknown) => v is T, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return fallback
    const parsed: unknown = JSON.parse(raw)
    return ok(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Nothing to do and nothing worth telling the user: the setting still
    // applies for this session, it just will not survive a reload.
  }
}

const isScale = (v: unknown): v is TextScale =>
  TEXT_SCALES.some((s) => s.value === v)
const isMode = (v: unknown): v is ThemeMode =>
  v === 'light' || v === 'dark' || v === 'system'

/**
 * Read once at module load and shared, so every subscriber starts on the same
 * value and the first paint is already at the right size.
 */
const state = {
  scale: read<TextScale>(SCALE_KEY, isScale, 1),
  mode: read<ThemeMode>(MODE_KEY, isMode, 'system'),
}

type Listener = () => void
const listeners = new Set<Listener>()
const announce = () => { for (const l of listeners) l() }

export function applyTextScale(scale: TextScale): void {
  document.documentElement.style.setProperty('--text-scale', String(scale))
}

export function setTextScale(scale: TextScale): void {
  state.scale = scale
  applyTextScale(scale)
  write(SCALE_KEY, scale)
  announce()
}

const prefersDark = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches

export const resolveMode = (mode: ThemeMode): 'light' | 'dark' =>
  mode === 'system' ? (prefersDark() ? 'dark' : 'light') : mode

/** Matches the two <html> attributes index.html ships with. */
const BODY_BG = { light: '#f3f3f5', dark: '#111015' }

/**
 * Put the resolved mode on <html> ourselves.
 *
 * Passing mode to Astryx's Theme is not enough on its own. The stone theme's
 * colours are light-dark() pairs, which resolve from the *inherited*
 * color-scheme, and the theme sheet sets that from
 * html[data-astryx-media] — an attribute index.html hardcodes to "light" and
 * nothing ever changed. Whether the mode took effect therefore came down to
 * which rule won the cascade on <html>, which is not something to leave to
 * chance. Setting the attribute and the inline color-scheme here decides it:
 * an inline style beats every sheet, and the attribute makes the markup true.
 *
 * theme-color follows too. It was two <meta> tags keyed on
 * prefers-color-scheme, so an installed app set to Light on a dark phone kept
 * a dark status bar.
 */
export function applyThemeMode(mode: ThemeMode): void {
  const resolved = resolveMode(mode)
  const root = document.documentElement
  root.setAttribute('data-astryx-media', resolved)
  root.style.colorScheme = resolved
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', BODY_BG[resolved])
}

export function setThemeMode(mode: ThemeMode): void {
  state.mode = mode
  applyThemeMode(mode)
  write(MODE_KEY, mode)
  announce()
}

/**
 * Applied before React mounts so nothing is drawn at the wrong size or in the
 * wrong colours first. index.html does the same thing in a blocking script so
 * there is no flash before the bundle arrives; this keeps them in step.
 */
export function initDisplay(): void {
  applyTextScale(state.scale)
  applyThemeMode(state.mode)

  // On 'system', follow the phone if it switches — including its own sunset
  // schedule, which changes under a running app rather than at startup.
  if (typeof matchMedia === 'function') {
    matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (state.mode === 'system') { applyThemeMode('system'); announce() }
    })
  }
}

export function useDisplay(): { scale: TextScale; mode: ThemeMode } {
  const [, bump] = useState(0)
  useEffect(() => {
    const l: Listener = () => bump((n) => n + 1)
    listeners.add(l)
    return () => { listeners.delete(l) }
  }, [])
  return { scale: state.scale, mode: state.mode }
}
